/*
 * Canonical public URL rules shared by the site's pages (bundled from src) and the API's sitemap job,
 * so a listed URL is always the URL the page serves. Bundled for the browser: CommonJS with no requires.
 * Property slugs fall back to the MLS ID without an address and omit absent parts; agent and office slugs
 * drop placeholder values; location segments are ASCII slugs (possessive `'s` becomes `s`, accents removed,
 * lowercase, other runs become `-`), so canonical URLs never need percent-encoding.
 */
const PROVINCES = ['ab', 'bc', 'mb', 'nb', 'nl', 'ns', 'nt', 'nu', 'on', 'pe', 'qc', 'sk', 'yt'];

const PAGE_NAMES = {
  properties: { en: 'properties-by-location', fr: 'proprietes-par-emplacement' },
  agents: { en: 'agents-by-location', fr: 'courtiers-par-emplacement' }
};

// Administrative area names (rural municipalities, wards, numbered districts) are not places people search for.
const ADMINISTRATIVE = /(^|-)(rural|ward|rm|twp|unorganized|admin)(-|$)|(^|-)m-d(-|$)|(^|-)county-of(-|$)|(^|-)no-\d+(-|$)/;
const PLACEHOLDER = /^(?:undefined|null|n\/?a|not available|unknown|none|-)$/i;

function fixPropertyLink(link, lowercase = true) {
  const normalized = link.replace(/[\s/]/g, '-').replace(/#/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return lowercase ? normalized.toLowerCase() : normalized;
}

function propertyPath(property, language = null) {
  const suppliedAddress = property.address?.trim();
  const useMlsFallback = !suppliedAddress && Boolean(property.mls_id);
  const address = suppliedAddress || property.mls_id || '';
  const postalCode = property.postal_code || '';
  const addressSlug = fixPropertyLink(String(address), !useMlsFallback);
  const locationSlug = fixPropertyLink([property.city, property.province, postalCode].filter(Boolean).join('-'));
  const link = [addressSlug, locationSlug].filter(Boolean).join('-');
  return (language === 'fr') ? `/fr/property/${property.id}/${link}` : `/property/${property.id}/${link}`;
}

function fixProfileLink(link) {
  return link.replace(/[\s/]/g, '-').replace(/#/g, '').replace(/-+/g, '-').toLowerCase();
}

function profileSlug(profile) {
  const parts = [profile.name, profile.address, profile.city, profile.province, profile.postal_code]
    .map(value => value === null || value === undefined ? '' : String(value).trim())
    .filter(value => value && !PLACEHOLDER.test(value));
  return fixProfileLink(parts.join('-')).replace(/^-|-$/g, '');
}

// kind: 'agent' or 'office'.
function profilePath(kind, profile, language = null) {
  const link = profileSlug(profile);
  return (language === 'fr') ? `/fr/${kind}/${profile.id}/${link}` : `/${kind}/${profile.id}/${link}`;
}

function isAdministrativeLocation(slug) {
  return ADMINISTRATIVE.test(slug);
}

function locationSlug(value) {
  return String(value ?? '')
    .replace(/['’](?=s\b)/gi, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function canonicalLocationPath(kind, language, province, city = '') {
  const prefix = language === 'fr' ? '/fr' : '';
  return `${prefix}/${PAGE_NAMES[kind][language]}/${province}${city ? `/${city}` : ''}`;
}

module.exports = { PROVINCES, PAGE_NAMES, propertyPath, profilePath, isAdministrativeLocation, locationSlug, canonicalLocationPath };
